import type {Three as THREE} from "#three-texture-streaming/three-exports"
import {ThreeTsl as TSL} from "#three-texture-streaming/three-exports"
import {assertNever} from "@cm/utils"
import type {ExplicitTextureStreamingMode} from "#three-texture-streaming/types"

type FloatNode = THREE.Node<"float">
type IntNode = THREE.Node<"int">
type Vec2Node = THREE.Node<"vec2">

// Scalar sRGB → linear conversion (matches CSS color spec)
export const srgbToLinearTsl = TSL.Fn(([x]: [FloatNode]) => {
    return TSL.select(
        x.lessThanEqual(TSL.float(0)),
        TSL.float(0),
        TSL.select(
            x.greaterThanEqual(TSL.float(1)),
            TSL.float(1),
            TSL.select(x.lessThan(TSL.float(0.04045)), x.div(TSL.float(12.92)), TSL.pow(x.add(TSL.float(0.055)).div(TSL.float(1.055)), TSL.float(2.4))),
        ),
    )
})

// ddx / ddy are derivatives of UV in texel space for all variants below; maxPixelsPerTexel is the maximum
// number of screen pixels a single texel is allowed to cover (multiplies into rho before the mip pick, so
// smaller than 1 forces a finer mip - oversampling - and larger allows a coarser one; 1 = no change).

// Finest mip / most tiles: based on slow-UV (stretched) direction — aliases in the fast-UV (compressed) direction - never blurs
export const computeMipLevelTsl_MinSingularValue = TSL.Fn(
    ([ddx, ddy, minMipLevel, maxMipLevel, maxPixelsPerTexel]: [Vec2Node, Vec2Node, FloatNode, FloatNode, FloatNode]) => {
        const a = ddx.x.mul(ddx.x).add(ddx.y.mul(ddx.y))
        const b = ddx.x.mul(ddy.x).add(ddx.y.mul(ddy.y))
        const c = ddy.x.mul(ddy.x).add(ddy.y.mul(ddy.y))
        const a_m_c = a.sub(c)
        const disc = TSL.sqrt(a_m_c.mul(a_m_c).add(TSL.float(4).mul(b).mul(b)))
        const lambdaMin = TSL.float(0.5).mul(a.add(c).sub(disc))
        const rho = TSL.sqrt(lambdaMin.max(TSL.float(0))).mul(maxPixelsPerTexel)
        // log2(0) is undefined in WGSL; clamp rho to ≥1 so the result is ≥0, which the outer
        // clamp maps to minMipLevel rather than producing NaN/0xFFFFFFFF on some GPU drivers
        return TSL.clamp(TSL.log2(rho.max(TSL.float(1))), minMipLevel, maxMipLevel).toVar()
    },
)

// Coarsest mip / fewest tiles: based on fast-UV (compressed) direction — blurry in the slow-UV (stretched) direction; matches standard isotropic GPU behaviour
export const computeMipLevelTsl_MaxSingularValue = TSL.Fn(
    ([ddx, ddy, minMipLevel, maxMipLevel, maxPixelsPerTexel]: [Vec2Node, Vec2Node, FloatNode, FloatNode, FloatNode]) => {
        const a = ddx.x.mul(ddx.x).add(ddx.y.mul(ddx.y))
        const b = ddx.x.mul(ddy.x).add(ddx.y.mul(ddy.y))
        const c = ddy.x.mul(ddy.x).add(ddy.y.mul(ddy.y))
        const a_m_c = a.sub(c)
        const disc = TSL.sqrt(a_m_c.mul(a_m_c).add(TSL.float(4).mul(b).mul(b)))
        const lambdaMax = TSL.float(0.5).mul(a.add(c).add(disc))
        const rho = TSL.sqrt(lambdaMax.max(TSL.float(0))).mul(maxPixelsPerTexel)
        return TSL.clamp(TSL.log2(rho.max(TSL.float(1))), minMipLevel, maxMipLevel).toVar()
    },
)

// Balanced: geometric mean of singular values = (ac - b²)^(1/4) — splits the difference; some aliasing and some blur in anisotropic cases; no eigendecomposition needed
export const computeMipLevelTsl_GeometricMean = TSL.Fn(
    ([ddx, ddy, minMipLevel, maxMipLevel, maxPixelsPerTexel]: [Vec2Node, Vec2Node, FloatNode, FloatNode, FloatNode]) => {
        const a = ddx.x.mul(ddx.x).add(ddx.y.mul(ddx.y))
        const b = ddx.x.mul(ddy.x).add(ddx.y.mul(ddy.y))
        const c = ddy.x.mul(ddy.x).add(ddy.y.mul(ddy.y))
        const rho = TSL.sqrt(TSL.sqrt(a.mul(c).sub(b.mul(b)).max(TSL.float(0)))).mul(maxPixelsPerTexel)
        return TSL.clamp(TSL.log2(rho.max(TSL.float(1))), minMipLevel, maxMipLevel).toVar()
    },
)

// Fast approximation of MaxSingularValue: max(|ddx|, |ddy|) — ignores cross-term b, close to MaxSingularValue in practice
export const computeMipLevelTsl_MaxAxis = TSL.Fn(
    ([ddx, ddy, minMipLevel, maxMipLevel, maxPixelsPerTexel]: [Vec2Node, Vec2Node, FloatNode, FloatNode, FloatNode]) => {
        const a = ddx.x.mul(ddx.x).add(ddx.y.mul(ddx.y))
        const c = ddy.x.mul(ddy.x).add(ddy.y.mul(ddy.y))
        const rho = TSL.sqrt(a.max(c)).mul(maxPixelsPerTexel)
        return TSL.clamp(TSL.log2(rho.max(TSL.float(1))), minMipLevel, maxMipLevel).toVar()
    },
)

export type ComputeMipLevelTsl = typeof computeMipLevelTsl_MinSingularValue

// Split a continuous mip level into integer and fractional parts.
// Takes the same maxMipLevel used in computeMipLevelTsl (= numMipLevels - 1).
// mipLevelInt is clamped to maxMipLevel-1 (= numMipLevels-2) so that the atlas
// mip1 slot — which stores the coarser tile content provided as coarserTileImage —
// is the only way the coarsest virtual mip level (N-1) is ever accessed. This avoids
// sampling the coarsest tile's own atlas slot, whose mip1 is never written
// (no coarserTileImage exists for N-1 tiles) and would contain garbage.
// At continuous mip = N-1: int = N-2, frac = 1.0 → 100% blend into atlas mip1
// of the N-2 slot, which holds the correct N-1 content.
export function splitMipLevelTsl(mipLevelContinuous: FloatNode, maxMipLevel: FloatNode): {mipLevelInt: IntNode; mipLevelFrac: FloatNode} {
    const maxMipIntF = maxMipLevel.sub(TSL.float(1)).max(TSL.float(0))
    const mipLevelInt = TSL.floor(mipLevelContinuous).min(maxMipIntF).toInt()
    const mipLevelFrac = mipLevelContinuous.sub(mipLevelInt.toFloat())
    return {mipLevelInt, mipLevelFrac}
}

export const getComputeMipLevelTsl = (streamingMode: ExplicitTextureStreamingMode): ComputeMipLevelTsl => {
    switch (streamingMode) {
        case "low-quality":
            return computeMipLevelTsl_MaxAxis
        case "high-quality":
            return computeMipLevelTsl_GeometricMean
        case "ultra-quality":
            return computeMipLevelTsl_MinSingularValue
        default:
            assertNever(streamingMode)
    }
}
